import { effect, inject, Injectable, signal } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { catchError, EMPTY, filter, forkJoin, retry, Subscription } from 'rxjs';
import { mapFileType } from '../helpers/dataset-utils';
import { mapUseCases } from '../helpers/hospital-utils';
import { AdminTableItemResponse, DataSetData } from '../models/api-responses';
import {
  HomePageHospital,
  HospitalDataSets,
  HospitalInfo,
} from '../models/hospital';
import { ISideBar, ISideBarContent } from '../models/sideBar';
import { User } from '../models/user';
import { ApiService } from './api.service';
import { ToastService } from './toast.service';

@Injectable({ providedIn: 'root' })
export class StateService {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private apiService = inject(ApiService);
  private toastService = inject(ToastService);

  private userState = signal<User | null>(null);
  public userState$ = this.userState.asReadonly();
  private sidebarState = signal<ISideBar>({});
  public sidebarState$ = this.sidebarState.asReadonly();
  private sideBarContents = signal<ISideBarContent[]>([]);
  public sideBarContent$ = this.sideBarContents.asReadonly();
  private hospitalsList = signal<HomePageHospital[]>([]);
  public hospitalsList$ = this.hospitalsList.asReadonly();
  private currentHospitalInfo = signal<HospitalInfo | null>(null);
  public currentHospitalInfo$ = this.currentHospitalInfo.asReadonly();
  private currentHospitalDataSets = signal<HospitalDataSets | null>(null);
  public currentHospitalDataSets$ = this.currentHospitalDataSets.asReadonly();
  private currentDataSetData = signal<DataSetData | null>(null);
  public currentDataSetData$ = this.currentDataSetData.asReadonly();
  private adminTableData = signal<AdminTableItemResponse[]>([]);
  public adminTableData$ = this.adminTableData.asReadonly();

  constructor() {
    this.initializeState();
    this.setSideBarStateFromUrl();

    effect((onCleanup) => {
      const state = this.sidebarState();

      const shouldFetch = state.dataSet !== undefined;

      if (shouldFetch) {
        const sub = this.getCurrentDataSetData(state);
        onCleanup(() => sub.unsubscribe());
      }
    });

    effect((onCleanup) => {
      const state = this.sidebarState();
      const hospitals = this.hospitalsList();

      if (state.hospital == null) {
        this.currentHospitalInfo.set(null);
      }

      const shouldFetch =
        state.hospital !== null &&
        state.hospital !== undefined &&
        state.dataSet === undefined;

      if (shouldFetch) {
        const sub = this.getHospitalInfo(state, hospitals);
        onCleanup(() => sub.unsubscribe());
      }
    });

    effect((onCleanup) => {
      const state = this.sidebarState();

      if (state.hospital == null) {
        this.currentHospitalInfo.set(null);
      }

      const shouldFetch =
        state.hospital !== null &&
        state.hospital !== undefined &&
        state.dataSet === undefined;

      if (shouldFetch) {
        const sub = this.getHospitalDataSets(state);
        onCleanup(() => sub.unsubscribe());
      }
    });
  }

  private getCurrentDataSetData(state: ISideBar): Subscription {
    return this.apiService
      .getDataSetTableData(state.dataSet!, state.snapshot)
      .pipe(
        retry(3),
        catchError((error) => {
          this.toastService.show('Error fetching dataset table data', 'danger');
          console.log(error);
          this.currentDataSetData.set(null);
          return EMPTY;
        }),
      )
      .subscribe({
        next: (tableData) => {
          this.currentDataSetData.set({ table: tableData } as DataSetData);

          // Unpinned dataset URLs resolve to the latest snapshot on the backend.
          // Replace the URL with the returned immutable snapshot URL so it can be shared safely.
          if (state.snapshot === undefined) {
            void this.router.navigate(
              ['/hospital', state.hospital, 'data-set', state.dataSet],
              {
                queryParams: { snapshot: tableData.snapshotId },
                replaceUrl: true,
              },
            );
          }
        },
      });
  }

  private getHospitalDataSets(state: ISideBar): Subscription {
    return this.apiService
      .getHospitalDataSets(state.hospital!)
      .pipe(
        retry(3),
        catchError((error) => {
          this.toastService.show('Error fetching hospital data sets', 'danger');
          console.error(error);
          this.currentHospitalDataSets.set(null);

          // Sync the sidebar, remove datasets for this hospital
          this.removeHospitalDatasetsFromSidebar(state.hospital!);

          return EMPTY;
        }),
      )
      .subscribe((data) => {
        const h = this.hospitalsList().find((x) => x.id === data.hospitalId);

        if (!h) {
          this.currentHospitalDataSets.set(null);
          return;
        }

        const datasets: HospitalDataSets = {
          hospitalId: data.hospitalId,
          dataSetLists: data.useCases.reduce(
            (previousValue, currentValue) => {
              previousValue[currentValue.useCase] = currentValue.dataSets.map(
                (x) => ({
                  dataSetId: x.dataSetId,
                  name: x.dataSetName,
                  fileName:
                    x.fileName +
                    (x.fileType != null ? mapFileType(x.fileType) : ''),
                  lastSync: currentValue.lastSync,
                }),
              );
              return previousValue;
            },
            {} as HospitalDataSets['dataSetLists'],
          ),
        };

        this.currentHospitalDataSets.set(datasets);
      });
  }

  private getHospitalInfo(
    state: ISideBar,
    hospitals: HomePageHospital[],
  ): Subscription {
    return this.apiService
      .getHospitalInfo(state.hospital!)
      .pipe(
        retry(3),
        catchError((error) => {
          this.toastService.show('Error fetching hospital info', 'danger');
          console.log(error);
          this.currentHospitalInfo.set(null);
          return EMPTY;
        }),
      )
      .subscribe((data) => {
        const h = hospitals.find((x) => x.id === data.id);

        this.currentHospitalInfo.set({
          id: data.id,
          name: h?.name ?? 'Hospital',
          address: data.address,
          latitude: data.latitude,
          longitude: data.longitude,
          useCases: h?.useCases ?? [],
        });
      });
  }

  setUserState(user: User) {
    this.userState.set(user);
    localStorage.setItem('userState', JSON.stringify(user));
  }

  getUserState(): User | null {
    return this.userState();
  }

  setSideBarStateFromUrl() {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        const hospitalId = parseInt(
          this.route.snapshot.firstChild?.paramMap.get('hospitalId') ?? '',
        );
        const dataSetId = parseInt(
          this.route.snapshot.firstChild?.paramMap.get('dataSetId') ?? '',
        );
        const snapshotId = parseInt(
          this.route.snapshot.firstChild?.queryParamMap.get('snapshot') ?? '',
        );
        const state: ISideBar = {
          hospital: !isNaN(hospitalId) ? hospitalId : undefined,
          dataSet: !isNaN(dataSetId) ? dataSetId : undefined,
          snapshot: !isNaN(snapshotId) ? snapshotId : undefined,
        };

        this.sidebarState.set(state);
      });
  }

  getSideBarContentState() {
    return localStorage.getItem('sideBarContents');
  }

  getHospitalData() {
    forkJoin({
      overview: this.apiService.getHospitalsOverview().pipe(retry(3)),
      list: this.apiService.getHospitalsList().pipe(retry(3)),
    }).subscribe({
      next: ({ overview, list }) => {
        const contents: ISideBarContent[] = overview.hospitals.map((y) => ({
          id: y.hospitalId,
          name: y.hospitalName,
          dataSets: y.dataSets.map((z) => ({
            id: z.dataSetId,
            name: z.dataSetName,
          })),
        }));
        this.sideBarContents.set(contents);
        localStorage.setItem('sideBarContents', JSON.stringify(contents));

        const hospitals: HomePageHospital[] = list.hospitals.map((y) => {
          const h = contents.find((z) => z.id === y.id);
          return h
            ? {
                id: h.id,
                name: h.name,
                useCases: mapUseCases(y.useCases),
                dataSetNumber: h.dataSets.length,
                country: y.country,
              }
            : {
                id: -1,
                name: '',
                useCases: [],
                dataSetNumber: 0,
                country: '',
              };
        });

        this.hospitalsList.set(hospitals.filter((x) => x.id !== -1));
      },
      error: (error) => {
        this.toastService.show('Error fetching hospitals', 'danger');
        console.log(error);

        // clear states
        this.sideBarContents.set([]);
        this.hospitalsList.set([]);
      },
    });
  }

  private removeHospitalDatasetsFromSidebar(hospitalId: number) {
    const currentContents = this.sideBarContents();

    // Find the hospital in sidebar contents and remove its datasets
    const updatedContents = currentContents.map((hospital) => {
      if (hospital.id === hospitalId) {
        return {
          ...hospital,
          dataSets: [],
        };
      }
      return hospital;
    });

    // Update the sidebar contents
    this.sideBarContents.set(updatedContents);
    localStorage.setItem('sideBarContents', JSON.stringify(updatedContents));
  }

  getAdminTableData() {
    this.apiService.getAdminTableData().subscribe({
      next: (res) => {
        this.adminTableData.set(res?.items ?? []);
      },
      error: (error) => {
        this.toastService.show('Error fetching admin view data', 'danger');
        console.log(error);
        this.adminTableData.set([]);
      },
    });
  }

  initializeState() {
    const user = localStorage.getItem('userState');
    if (user) {
      this.userState.set(JSON.parse(user));
      const sideBarContents = localStorage.getItem('sideBarContents');
      if (sideBarContents) {
        this.sideBarContents.set(JSON.parse(sideBarContents));
      }
    }
  }

  clearState() {
    this.userState.set(null);
    this.sideBarContents.set([]);
    localStorage.removeItem('userState');
    localStorage.removeItem('sideBarContents');
  }
}
